import { Popup } from "@progress/kendo-react-popup";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Code, Info } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { ConditionAttributeOption } from "@/components/ConditionBuilder/types";
import { getOptionLabel } from "@/components/ConditionBuilder/utils/optionLabel";
import useOnClickOutside from "@/hooks/useOnClickOutside";

interface HierarchicalAttributeDropdownProps {
  value: ConditionAttributeOption | null | undefined;
  onChange: (item: ConditionAttributeOption) => void;
  attributes: ConditionAttributeOption[];
  label?: string;
  disabled?: boolean;
}

const HierarchicalAttributeDropdown: React.FC<
  HierarchicalAttributeDropdownProps
> = ({
  value,
  onChange,
  attributes,
  label = "Attribute",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [level, setLevel] = useState<"groups" | "items">("groups");
  const [activeGroup, setActiveGroup] =
    useState<ConditionAttributeOption | null>(null);
  const [filter, setFilter] = useState("");

  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const filterInputRef = useRef<HTMLInputElement | null>(null);
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null);

  const { data: fetchedSubItems = [], isFetching } = useQuery({
    queryKey: ["hierarchicalSubAttributes", activeGroup?.id],
    queryFn: () =>
      activeGroup!
        .fetchSubAttributes!.method(
          activeGroup!.fetchSubAttributes!.params ?? ({} as never),
        )
        .then(r => r.map(activeGroup!.fetchSubAttributes!.parseOption)),
    enabled: !!activeGroup?.fetchSubAttributes,
  });

  const subItems = activeGroup?.fetchSubAttributes
    ? fetchedSubItems
    : (activeGroup?.subAttributes ?? []);

  const handleClose = () => {
    setIsOpen(false);
    setFilter("");
  };

  /**
   * Escape closes the popup and hands focus back to the field. Only
   * `preventDefault()` - never `stopPropagation()`: an enclosing SlidePanel's
   * focus trap reads a prevented Escape as already handled and stays open,
   * while any other document listener still sees the key.
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    handleClose();
    triggerButtonRef.current?.focus();
  };

  useOnClickOutside(popupRef, () => handleClose(), {
    current: triggerRef.current,
  });

  useEffect(() => {
    if (isOpen && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [isOpen]);

  function handleOpen() {
    if (disabled) return;

    // Edit-mode init: if value.type matches a group id, open directly at Level 2
    if (value?.type) {
      const matchedGroup = attributes.find(
        a => a.id === value.type && (a.fetchSubAttributes || a.subAttributes),
      );
      if (matchedGroup) {
        setActiveGroup(matchedGroup);
        setLevel("items");
        setFilter("");
        setIsOpen(true);
        return;
      }
    }

    setLevel("groups");
    setActiveGroup(null);
    setFilter("");
    setIsOpen(true);
  }

  function handleGroupClick(group: ConditionAttributeOption) {
    setActiveGroup(group);
    setLevel("items");
    setFilter("");
  }

  function handleBack() {
    setLevel("groups");
    setActiveGroup(null);
    setFilter("");
  }

  function handleSelect(item: ConditionAttributeOption) {
    const resolved = activeGroup?.resolveSubAttribute?.(item);
    onChange(resolved ? { ...item, ...resolved } : item);
    handleClose();
  }

  const filteredGroups = attributes.filter(a =>
    getOptionLabel(a).toLowerCase().includes(filter.toLowerCase()),
  );

  const filteredSubItems = subItems.filter((a: ConditionAttributeOption) =>
    getOptionLabel(a).toLowerCase().includes(filter.toLowerCase()),
  );

  const hasValue = !!value;

  return (
    <div className="k-form-field condition-builder__attribute-dropdown">
      <span
        className={`k-floating-label-container${hasValue ? "" : " k-empty"}`}
        ref={triggerRef}
        onClick={handleOpen}
        style={{ width: "100%", cursor: disabled ? "default" : "pointer" }}
      >
        <span
          className={`k-dropdownlist k-input k-input-md k-rounded-md k-input-solid${disabled ? " k-disabled" : ""}`}
          style={{ width: "100%" }}
        >
          <span className="k-input-inner">{getOptionLabel(value)}</span>
          <button
            ref={triggerButtonRef}
            className="k-button k-button-md k-button-solid k-button-solid-base k-icon-button k-input-button"
            type="button"
            tabIndex={-1}
            disabled={disabled}
            style={{ border: 0 }}
          >
            <span className="k-icon k-svg-icon k-svg-i-caret-alt-down k-button-icon">
              <svg
                aria-hidden="true"
                focusable="false"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
              >
                <path d="M256 352 128 160h256z"></path>
              </svg>
            </span>
          </button>
        </span>
        <label className="k-floating-label">{label}</label>
      </span>

      <Popup
        anchor={triggerRef.current}
        show={isOpen}
        popupClass="condition-builder__attribute-popup k-popup k-list-container k-dropdownlist-popup"
        style={{ width: triggerRef.current?.offsetWidth }}
        // The popup changes height between the groups and items levels, while
        // searching and once sub-attributes load. Kendo only re-runs its
        // collision flip when this key changes - without it, a popup flipped
        // above the field for the tall items list keeps that top edge after
        // Back, floating away from the field.
        contentKey={`${level}:${activeGroup?.id ?? ""}:${filter}:${isFetching}:${subItems.length}`}
      >
        {/* Keyboard handler for the popup's own controls (filter, Back). */}
        <div
          ref={popupRef}
          className="k-list-container"
          onKeyDown={handleKeyDown}
        >
          {/* Filter input */}
          <div className="condition-builder__attribute-filter k-list-filter">
            <span className="k-textbox k-input k-input-md k-input-solid k-rounded-md k-focus k-searchbox">
              <span className="k-input-prefix k-input-prefix-horizontal">
                <span
                  className="k-icon k-svg-icon k-svg-i-search k-input-icon"
                  aria-hidden="true"
                >
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                  >
                    <path d="M365.3 320h-22.7l-26.7-26.7C338.5 265.7 352 230.4 352 192c0-88.4-71.6-160-160-160S32 103.6 32 192s71.6 160 160 160c38.4 0 73.7-13.5 101.3-36.1l26.7 26.7v22.7L434.7 480l45.3-45.3zM64 192c0-70.7 57.3-128 128-128s128 57.3 128 128-57.3 128-128 128S64 262.7 64 192"></path>
                  </svg>
                </span>
              </span>
              <input
                ref={filterInputRef}
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder="Search..."
                className="k-input-inner"
              />
            </span>
          </div>

          {level === "groups" ? (
            <ul className="k-list k-list-md">
              {filteredGroups.map(attr => (
                <li
                  key={attr.id}
                  className={`k-list-item${value?.id === attr.id && !attr.fetchSubAttributes && !attr.subAttributes ? " k-selected" : ""}`}
                  onClick={() => {
                    if (attr.fetchSubAttributes || attr.subAttributes) {
                      handleGroupClick(attr);
                    } else {
                      handleSelect(attr);
                    }
                  }}
                >
                  <span className="k-list-item-text">
                    {getOptionLabel(attr)}
                  </span>
                  {attr.fetchSubAttributes || attr.subAttributes ? (
                    <ChevronRight
                      size={14}
                      className="condition-builder__attribute-item-icon"
                    />
                  ) : attr.itemIcon === "info" ? (
                    <span
                      title={attr.hint}
                      className="condition-builder__attribute-item-icon"
                    >
                      <Info size={14} />
                    </span>
                  ) : attr.itemIcon === "function" ? (
                    <span
                      title={attr.hint}
                      className="condition-builder__attribute-item-icon"
                    >
                      <Code size={14} />
                    </span>
                  ) : attr.hint ? (
                    <span
                      title={attr.hint}
                      className="condition-builder__attribute-item-icon"
                    >
                      <Info size={14} />
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <>
              <button
                className="condition-builder__attribute-back"
                onClick={handleBack}
                type="button"
              >
                <ChevronLeft size={14} />
                {getOptionLabel(activeGroup)}
              </button>
              {isFetching ? (
                <div className="condition-builder__attribute-loading">
                  Loading...
                </div>
              ) : (
                <ul className="k-list k-list-md">
                  {filteredSubItems.map((item: ConditionAttributeOption) => (
                    <li
                      key={item.id}
                      className={`k-list-item${value?.id === item.id ? " k-selected" : ""}`}
                      onClick={() => handleSelect(item)}
                    >
                      <span className="k-list-item-text">
                        {getOptionLabel(item)}
                      </span>
                      {item.hint && (
                        <span
                          title={item.hint}
                          className="condition-builder__attribute-item-icon"
                        >
                          <Info size={14} />
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </Popup>
    </div>
  );
};

export default HierarchicalAttributeDropdown;
